/**
 * @page ProzesseAbteilungenPage
 * @route /prozesse/abteilungen
 * @description Prozess-Abteilungen Übersicht — Department-Karten mit Prozess-Counts + Fortschrittsbalken.
 * @roles admin, ops_manager
 * @i18n prozesse.*
 * @endpoints GET /api/prozesse/abteilungen, POST /api/prozesse/abteilungen
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Building2, ChevronRight, FileText, CheckCircle, Clock, Archive, Plus, X, Loader2, UserCog } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function getColors() {
  return { bg: 'var(--bg-inset, #F8F9FC)', color: 'var(--text-secondary, #4B5563)' };
}

export default function ProzesseAbteilungenPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [abteilungen, setAbteilungen] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editAbt, setEditAbt] = useState(null); // Abteilung fuer Owner-Edit

  const load = () => {
    fetch(`${API}/api/prozesse/abteilungen`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { abteilungen: [] })
      .then(d => setAbteilungen(d.abteilungen || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    fetch(`${API}/api/prozesse/stats`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStats(d); })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // #1724 P1 Schritt 3: KPI-Cards oben (Datenquelle wie die gelöschte Übersicht)
  const kpis = [
    { label: t('prozesse.stats.gesamt'),    value: stats?.gesamt ?? '–',    Icon: FileText,    color: 'text-[#338edc]' },
    { label: t('prozesse.stats.aktiv'),     value: stats?.aktiv ?? '–',     Icon: CheckCircle, color: 'text-[#10B981]' },
    { label: t('prozesse.stats.inReview'),  value: stats?.in_review ?? '–', Icon: Clock,       color: 'text-[#F59E0B]' },
    { label: t('prozesse.stats.entwuerfe'), value: stats?.entwuerfe ?? '–', Icon: Archive,     color: 'text-[#6366F1]' },
  ];

  // Calculate totals
  const totalProzesse = abteilungen.reduce((s, a) => s + (a.prozess_count || 0), 0);
  const totalAktiv = abteilungen.reduce((s, a) => s + (a.aktiv_count || 0), 0);

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 size={24} className="text-[var(--text-secondary,#4B5563)]" />
          <div>
            <h1 className="text-xl font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.abteilungen')}</h1>
            <p className="text-xs text-[var(--text-secondary,#4B5563)] mt-0.5">
              {totalProzesse} {t('prozesse.stats.gesamt')} · {totalAktiv} {t('prozesse.stats.aktiv')}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors"
        >
          <Plus size={16} />
          {t('prozesse.abt.neu', 'New Department')}
        </button>
      </div>

      {/* KPI Cards (#1724 — Hybrid-Landing, gleiche Optik + Quelle wie die frühere Übersicht) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => (
          <div key={i} className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-5">
            <div className="flex items-center gap-2 mb-3">
              <kpi.Icon size={16} className={kpi.color} />
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary,#4B5563)]">{kpi.label}</span>
            </div>
            <div className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)]">{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Department Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
        </div>
      ) : abteilungen.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
            <Building2 size={28} className="text-[var(--text-muted,#9CA3AF)]" />
          </div>
          <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)] mb-1">
            {t('prozesse.abt.emptyTitle', 'No departments yet')}
          </h3>
          <p className="text-sm text-[var(--text-tertiary,#6B7280)] max-w-sm mb-5">
            {t('prozesse.abt.emptyDesc', 'Create your first department to start documenting processes.')}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors"
          >
            <Plus size={16} />
            {t('prozesse.abt.neu', 'New Department')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {abteilungen.map(abt => {
            const colors = getColors(abt.name);
            const total = abt.prozess_count || 0;
            const aktiv = abt.aktiv_count || 0;
            const pct = total > 0 ? Math.round((aktiv / total) * 100) : 0;

            return (
              <div
                key={abt.id}
                onClick={() => navigate(`/prozesse/abteilungen/${abt.slug}`)}
                className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-6 cursor-pointer hover:border-[#338edc] transition-all group"
              >
                {/* Icon + Name */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
                      <Building2 size={20} style={{ color: colors.color }} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{abt.name}</h3>
                      {abt.beschreibung && (
                        <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-0.5 line-clamp-1">{abt.beschreibung}</p>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-[var(--text-tertiary,#6B7280)] group-hover:text-[#338edc] transition-colors mt-1" />
                </div>

                {/* Prozess-Owner */}
                <div className="flex items-center gap-2 mb-3 text-xs">
                  <UserCog size={12} className="text-[var(--text-tertiary,#6B7280)]" />
                  <span className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.abt.owner', 'Prozess-Owner')}:</span>
                  <span className="text-[var(--text-secondary,#4B5563)] font-medium">
                    {abt.owner_vorname ? `${abt.owner_vorname} ${abt.owner_nachname || ''}` : <em className="text-[var(--text-tertiary,#6B7280)]">{t('prozesse.abt.no_owner', 'nicht gesetzt')}</em>}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditAbt(abt); }}
                    className="ml-auto text-[#338edc] hover:underline text-[11px]">
                    {t('common.edit', 'Bearbeiten')}
                  </button>
                </div>

                {/* Stats Row */}
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-1.5">
                    <FileText size={12} className="text-[var(--text-tertiary,#6B7280)]" />
                    <span className="text-xs text-[var(--text-secondary,#4B5563)]">
                      {total} {t('prozesse.stats.gesamt')}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle size={12} className="text-[#10B981]" />
                    <span className="text-xs text-[var(--text-secondary,#4B5563)]">
                      {aktiv} {t('prozesse.stats.aktiv')}
                    </span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full h-1.5 rounded-full bg-[var(--bg-inset,#F3F4F6)]">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? '#10B981' : pct >= 40 ? '#338edc' : '#F59E0B' }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-[var(--text-tertiary,#6B7280)]">{pct}% {t('prozesse.stats.aktiv')}</span>
                  <span className="text-[10px] text-[var(--text-tertiary,#6B7280)]">{abt.bereiche_count || 0} {t('prozesse.bereiche')}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Department Modal */}
      {showModal && (
        <NewAbteilungModal
          t={t}
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); load(); }}
        />
      )}

      {/* Owner-Edit Modal */}
      {editAbt && (
        <OwnerEditModal
          t={t}
          abteilung={editAbt}
          onClose={() => setEditAbt(null)}
          onSaved={() => { setEditAbt(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Owner-Edit Modal (#920) ──
function OwnerEditModal({ t, abteilung, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [ownerId, setOwnerId] = useState(abteilung.prozess_owner_id || '');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/users`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        const active = (rows || []).filter(u => u.aktiv !== false && ['administrator', 'admin', 'ops_manager', 'cs_teamlead'].includes(u.rolle));
        setUsers(active);
      })
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API}/api/prozesse/abteilungen/${abteilung.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ prozess_owner_id: ownerId || null }),
      });
      if (r.ok) onSaved();
      else { const d = await r.json().catch(() => ({})); setError(d.error || 'Error'); }
    } catch { setError('Network error'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.abt.editOwnerTitle', 'Prozess-Owner setzen')}</h2>
            <p className="text-xs text-[var(--text-tertiary,#6B7280)] mt-0.5">{abteilung.name}</p>
          </div>
          <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          {error && <div className="text-xs text-[#EF4444] bg-[#EF44440D] border border-[#EF444433] rounded-lg px-3 py-2">{error}</div>}
          <p className="text-xs text-[var(--text-tertiary,#6B7280)]">
            {t('prozesse.abt.ownerHint', 'Der Prozess-Owner erhaelt Notifications bei neuen Vorschlaegen zu Prozessen dieser Abteilung.')}
          </p>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-1.5">
              {t('prozesse.abt.owner', 'Prozess-Owner')}
            </label>
            {loadingUsers ? (
              <div className="text-xs text-[var(--text-tertiary,#6B7280)]"><Loader2 size={12} className="inline animate-spin mr-1" /> {t('common.loading', 'Lade…')}</div>
            ) : (
              <select value={ownerId} onChange={e => setOwnerId(e.target.value)}
                className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm focus:outline-none focus:border-[#338edc]">
                <option value="">— {t('prozesse.abt.no_owner', 'nicht gesetzt')} —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.vorname} {u.nachname} · {u.rolle}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
              {t('common.cancel', 'Abbrechen')}
            </button>
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {t('common.save', 'Speichern')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Create Department Modal ──
function NewAbteilungModal({ t, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/prozesse/abteilungen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), beschreibung: beschreibung.trim() || null }),
      });
      if (res.ok) {
        onCreated();
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Error');
      }
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">{t('prozesse.abt.neu', 'New Department')}</h2>
          <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="text-xs text-[#EF4444] bg-[#EF44440D] border border-[#EF444433] rounded-lg px-3 py-2">{error}</div>
          )}
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-1.5">
              {t('prozesse.abt.name', 'Name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('prozesse.abt.namePlaceholder', 'e.g. Inbound, Outbound, IT')}
              className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc]"
              autoFocus
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary,#4B5563)] mb-1.5">
              {t('prozesse.abt.beschreibung', 'Description')}
            </label>
            <textarea
              value={beschreibung}
              onChange={e => setBeschreibung(e.target.value)}
              placeholder={t('prozesse.abt.beschreibungPlaceholder', 'Short description of this department...')}
              rows={3}
              className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc] resize-none"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
              {t('common.cancel', 'Cancel')}
            </button>
            <button type="submit" disabled={saving || !name.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {t('prozesse.abt.create', 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
